import React from "react";
import { Paper, Box, Link, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import {
  InsertPhoto,
  PictureAsPdf,
  TableChart,
  VideoLibrary,
  Audiotrack,
  Description,
  InsertDriveFile,
} from "@material-ui/icons";
import { useSelector } from "../../../store";

const LoadChatData = (props) => {
  const { identity, chatHistory } = useSelector((state) => {
    return state.room;
  });

  // console.log("chat-history-data", chatHistory);

  var chatContent = chatHistory.map((chat, index) =>
    <ChatItem key={index} chatData={chat} currentUser={identity} />
  );

  return (chatContent);
};

function ChatItem(props) {
  return (
    <Box sx={{
      p: 1,
      m: 1,
      display: "flex",
      justifyContent: props.chatData.userId == props.currentUser ? "flex-end" : "flex-start"

    }}>
      <Paper
        elevation={0}
        component="span"
        variant="outlined"
        sx={{
          p: 1,
          backgroundColor: props.chatData.userId != props.currentUser && "#e8f4f8",
          // color: props.chatData.userId != props.currentUser && "#FFFFFF",
          maxWidth: "340px",
          wordWrap: "break-word",
        }} >
        {props.chatData.file != null ?
          <MediaMessage chatData={props.chatData} currentUser={props.currentUser} /> :
          <TextMessage chatData={props.chatData} currentUser={props.currentUser} />
        }
      </Paper>
    </Box>);
}

function TextMessage(props) {
  return (
    <Box>{props.chatData.userId == props.currentUser ? "you: " : props.chatData.userId + ": "}
      {props.chatData.text.includes("http") ? <Link href={props.chatData.text} color="inherit" target="_blank" rel="noopener">{props.chatData.text}</Link> : props.chatData.text}</Box>
  );
}

function getFileIcon(fileName) {
  if (!fileName) return InsertDriveFile;
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension)) {
    return InsertPhoto;
  }
  
  // PDF
  if (extension === 'pdf') {
    return PictureAsPdf;
  }
  
  // Excel/Spreadsheet
  if (['xls', 'xlsx', 'csv'].includes(extension)) {
    return TableChart;
  }
  
  // Video
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(extension)) {
    return VideoLibrary;
  }
  
  // Audio
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(extension)) {
    return Audiotrack;
  }
  
  // Word documents
  if (['doc', 'docx'].includes(extension)) {
    return Description;
  }
  
  // Default
  return InsertDriveFile;
}

function getFileIconColor(fileName) {
  if (!fileName) return '#757575';
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension)) {
    return '#4caf50';
  }
  
  // PDF
  if (extension === 'pdf') {
    return '#f44336';
  }
  
  // Excel/Spreadsheet
  if (['xls', 'xlsx', 'csv'].includes(extension)) {
    return '#2196f3';
  }
  
  // Video
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(extension)) {
    return '#9c27b0';
  }
  
  // Audio
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(extension)) {
    return '#ff9800';
  }
  
  // Word documents
  if (['doc', 'docx'].includes(extension)) {
    return '#2196f3';
  }
  
  // Default
  return '#757575';
}

function isImageFile(fileName) {
  if (!fileName) return false;
  const extension = fileName.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension);
}

function MediaMessage(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fileName = props.chatData.text || '';
  const FileIcon = getFileIcon(fileName);
  const iconColor = getFileIconColor(fileName);
  const isImage = isImageFile(fileName);
  
  const handleFileDownload = async (e) => {
    // Disable download on mobile
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    const fileUrl = props.chatData.s3FileUrl;
    if (!fileUrl) return;
    
    try {
      // Fetch the file as a blob
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element for download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: open in new tab if download fails (only on desktop)
      if (!isMobile) {
        window.open(fileUrl, '_blank');
      }
    }
  };
  
  const fileContent = (
    <>
      {isImage ? (
        <Box
          component="img"
          src={props.chatData.s3FileUrl}
          alt={fileName}
          sx={{
            maxWidth: '100%',
            maxHeight: 300,
            borderRadius: 1,
            objectFit: 'contain',
            mb: 1,
            display: 'block',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: 1,
            backgroundColor: '#f5f5f5',
            mb: 1,
            border: '1px solid #e0e0e0',
          }}
        >
          <FileIcon 
            sx={{ 
              fontSize: 48, 
              color: iconColor 
            }} 
          />
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: 'text.primary',
          wordBreak: 'break-word',
          maxWidth: '100%',
          fontSize: '0.875rem',
        }}
      >
        {fileName}
      </Typography>
    </>
  );
  
  return (
    <Box>
      <Box sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
        {props.chatData.userId == props.currentUser ? "you" : props.chatData.userId}:
      </Box>
      {isMobile ? (
        // On mobile: just display, no click/download functionality
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {fileContent}
        </Box>
      ) : (
        // On desktop: clickable with download functionality
        <Box
          onClick={handleFileDownload}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            '&:active': {
              opacity: 0.6,
            }
          }}
        >
          {fileContent}
        </Box>
      )}
    </Box>
  );
}

export default LoadChatData;